import { createContext, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
// firestore
import { collection, doc, onSnapshot, query, setDoc } from "firebase/firestore";
import { db } from "../services/firebase";
// settings
import actionTypes from "../common/actionTypes";
import {
  APP_ROUTES,
  DATABASE_STATUTES,
  defaultBISEmailValues,
  defaultPOEmailValues,
  defaultPOSMSValues,
  defaultPOShippingDelayEmailValues,
} from "../common/constants";
// context
import useAuthContext from "../hooks/useAuthContext";
import useActivityLogsContext from "../hooks/useActivityLogsContext";
// reducers
import { automationsReducer } from "../reducers";
//
import axios from "../utils/axios";
// constants
import { LOG_TYPES } from "./ActivityLogsContext";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";

const SHOPS = "Shops";
const AUTOMATIONS = "Automations";
const UPDATE_LOGO_URL = "shop/logo/upload";
const SHIPPING_DELAY_EMAILS_URL = "api/notifications/poShippingDelay";

const initialState = {
  // Pre-order email
  po: {
    email: defaultPOEmailValues,
    sms: defaultPOSMSValues,
    shippingEmail: defaultPOShippingDelayEmailValues,
  },
  // Back-in-stock email.
  bis: {
    email: defaultBISEmailValues,
    // sms
  },
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const AutomationsContext = createContext({
  ...initialState,
  fetchAutomations: () => {},
  editAutomation: () => Promise.resolve(),
  sendShippingDelayEmails: () => Promise.resolve(),
});

const AutomationsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(automationsReducer, initialState);

  const automationsRef = useRef(null);

  const { user } = useAuthContext();
  const { writeLog } = useActivityLogsContext();

  const { pathname } = useLocation();
  const fetchingIsActive =
    pathname.startsWith(APP_ROUTES.ORDERS.ROOT) || pathname.startsWith(APP_ROUTES.NOTIFICATIONS.ROOT);

  useEffect(() => {
    if (user?.domain && fetchingIsActive) {
      dispatch({
        type: actionTypes.FETCH_AUTOMATIONS,
      });

      automationsRef.current = collection(db, SHOPS, user.domain, AUTOMATIONS);

      try {
        return onSnapshot(query(automationsRef.current), (snapshot) => {
          dispatch({
            type: actionTypes.FETCH_AUTOMATIONS_SUCCESS,
            payload: snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            })),
          });
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_AUTOMATIONS_FAILURE,
          payload: error,
        });
      }
    }
  }, [user?.domain, fetchingIsActive, dispatch]);

  const editAutomation = useCallback(
    async ({ id, data, type }) => {
      try {
        const { logo, ...rest } = data;
        let params = { ...rest };

        console.log("editAutomation", JSON.stringify({ id, data, type }, null, 2));

        if (user) {
          if (logo && logo !== "") {
            if (logo.startsWith("data:")) {
              await axios.post(UPDATE_LOGO_URL, {
                file: logo,
              });
            } else {
              params.logo = logo;
            }
          } else {
            params.logo = "";
          }

          await setDoc(doc(db, SHOPS, user.domain, AUTOMATIONS, id), params, {
            merge: true,
          });

          switch (type) {
            case "PO_EMAIL":
              await writeLog(LOG_TYPES.UPDATE_PO_NOTIFICATION_EMAIL);
              break;

            case "PO_SHIPPING_EMAIL":
            case "PO_SHIPPING_DELAY":
              await writeLog(LOG_TYPES.UPDATE_SHIPPING_DELAY_EMAIL);
              break;

            case "BIS_EMAIL":
              await writeLog(LOG_TYPES.UPDATE_BACK_IN_STOCK_EMAIL);
              break;

            default:
              break;
          }
        }
      } catch (error) {
        dispatch({
          type: actionTypes.EDIT_AUTOMATIONS_FAILURE,
          payload: error,
        });
        throw error;
      }
    },
    [user?.domain]
  );

  const sendShippingDelayEmails = async ({ orders, variables }) => {
    try {
      await axios.post(SHIPPING_DELAY_EMAILS_URL, {
        orders,
        ...variables,
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject();
    }
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      editAutomation,
      sendShippingDelayEmails,
    }),
    [editAutomation, state]
  );

  return <AutomationsContext.Provider value={memoizedState}>{children}</AutomationsContext.Provider>;
};

export { AutomationsContext, AutomationsProvider };
